import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Seo from '@/components/base/Seo';
import MovieCard from '@/components/feature/MovieCard';
import FilterPanel from '@/pages/category/components/FilterPanel';
import Pagination from '@/pages/category/components/Pagination';
import { getMoviesByType } from '@/mocks/movies';
import { sortOptions, type SortValue } from '@/mocks/filters';

const typeConfig: Record<string, { title: string; desc: string; seoTitle: string; seoDesc: string }> = {
  movie: {
    title: '电影大全',
    desc: '汇聚院线热映与经典佳作，覆盖动作、爱情、科幻、悬疑等多种类型',
    seoTitle: '电影分类 - 菠萝蜜网',
    seoDesc: '菠萝蜜网电影大全，覆盖动作、爱情、科幻、悬疑等多种类型电影资源，支持按地区、年份筛选与排序，快速找到你喜欢的电影。',
  },
  tv: {
    title: '电视剧全集',
    desc: '古装、都市、悬疑、青春题材一网打尽，追剧更新不断档',
    seoTitle: '电视剧分类 - 菠萝蜜网',
    seoDesc: '菠萝蜜网电视剧全集，古装、都市、悬疑、青春题材剧集持续更新，支持按类型、地区、年份筛选，快速找到热门剧集。',
  },
  variety: {
    title: '综艺精选',
    desc: '美食、音乐、户外、喜剧综艺精选合集，陪你轻松解压',
    seoTitle: '综艺分类 - 菠萝蜜网',
    seoDesc: '菠萝蜜网综艺精选，涵盖美食、音乐、户外、喜剧等热门综艺节目，支持多维度筛选与排序，发现更多有趣综艺。',
  },
};

const PAGE_SIZE = 8;

export default function CategoryPage() {
  const { type = 'movie' } = useParams<{ type: string }>();
  const config = typeConfig[type] ?? typeConfig.movie;

  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [sort, setSort] = useState<SortValue>('latest');
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useEffect(() => {
    setSelectedGenres([]);
    setSelectedRegions([]);
    setSelectedYears([]);
    setSort('latest');
    setPage(1);
    window.scrollTo({ top: 0 });
  }, [type]);

  const baseMovies = useMemo(() => getMoviesByType(type as 'movie' | 'tv' | 'variety'), [type]);

  const filtered = useMemo(() => {
    let list = baseMovies.filter((movie) => {
      const genreMatch = selectedGenres.length === 0 || movie.genres.some((g) => selectedGenres.includes(g));
      const regionMatch = selectedRegions.length === 0 || selectedRegions.includes(movie.region);
      const yearMatch = selectedYears.length === 0 || selectedYears.includes(movie.year);
      return genreMatch && regionMatch && yearMatch;
    });

    list = [...list].sort((a, b) => {
      if (sort === 'latest') return b.year - a.year;
      if (sort === 'rating') return b.rating - a.rating;
      return Number(b.hot) - Number(a.hot) || b.rating - a.rating;
    });

    return list;
  }, [baseMovies, selectedGenres, selectedRegions, selectedYears, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pagedMovies = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [selectedGenres, selectedRegions, selectedYears, sort]);

  const resetFilters = () => {
    setSelectedGenres([]);
    setSelectedRegions([]);
    setSelectedYears([]);
  };

  const toggleGenre = (v: string) => {
    setSelectedGenres((prev) => (prev.includes(v) ? prev.filter((item) => item !== v) : [...prev, v]));
  };
  const toggleRegion = (v: string) => {
    setSelectedRegions((prev) => (prev.includes(v) ? prev.filter((item) => item !== v) : [...prev, v]));
  };
  const toggleYear = (v: number) => {
    setSelectedYears((prev) => (prev.includes(v) ? prev.filter((item) => item !== v) : [...prev, v]));
  };

  const filterProps = {
    selectedGenres,
    selectedRegions,
    selectedYears,
    onToggleGenre: toggleGenre,
    onToggleRegion: toggleRegion,
    onToggleYear: toggleYear,
    onReset: resetFilters,
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Seo title={config.seoTitle} description={config.seoDesc} keywords={`菠萝蜜网,${config.title},影视资源`} />
      <Navbar />
      <main className="flex-1">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
          <nav className="text-xs text-foreground-500 flex items-center gap-1.5 mb-4" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">{config.title}</span>
          </nav>

          <h1 className="text-2xl md:text-3xl font-bold text-foreground-900">{config.title}</h1>
          <p className="mt-2 text-sm text-foreground-500">{config.desc}</p>

          <div className="mt-6 flex flex-col lg:flex-row gap-6">
            <aside className="hidden lg:block w-full lg:w-64 shrink-0">
              <FilterPanel {...filterProps} />
            </aside>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <button
                  onClick={() => setMobileFilterOpen(true)}
                  className="lg:hidden flex items-center gap-1.5 px-3 py-2 rounded-md bg-background-100 text-sm text-foreground-700 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-filter-3-line"></i> 筛选
                </button>
                <p className="text-sm text-foreground-500">共 {filtered.length} 部作品</p>
                <div className="flex items-center gap-1 ml-auto bg-background-100 rounded-full p-1">
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setSort(opt.value)}
                      className={`px-3 py-1.5 text-xs rounded-full whitespace-nowrap cursor-pointer transition-colors ${
                        sort === opt.value ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-primary-600'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {pagedMovies.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                    {pagedMovies.map((movie) => (
                      <MovieCard key={movie.id} movie={movie} />
                    ))}
                  </div>
                  <Pagination currentPage={page} totalPages={totalPages} onChange={setPage} />
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <i className="ri-film-line text-5xl text-background-300 mb-4"></i>
                  <p className="text-foreground-600">抱歉，没有找到相关影视作品</p>
                  <button
                    onClick={resetFilters}
                    className="mt-4 px-5 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm cursor-pointer whitespace-nowrap"
                  >
                    清空筛选条件
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {mobileFilterOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-foreground-950/50" onClick={() => setMobileFilterOpen(false)}></div>
          <div className="absolute right-0 top-0 bottom-0 w-[85%] max-w-sm bg-background-50 overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-foreground-900">筛选条件</h2>
              <button onClick={() => setMobileFilterOpen(false)} className="w-8 h-8 flex items-center justify-center cursor-pointer" aria-label="关闭">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <FilterPanel {...filterProps} />
            <button
              onClick={() => setMobileFilterOpen(false)}
              className="mt-5 w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              查看结果
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}